/** Controls how fractional cents are displayed by {@link formatUSCurrency}. */
export type CentsDisplay = "always" | "never" | "auto";

/**
 * Formats a numeric value as USD currency (e.g. "$15,000.00").
 *
 * Values are coerced with `Number(...)` first: quote amounts often arrive from
 * the API as strings, and `String.prototype.toLocaleString` silently ignores
 * number-formatting options, producing unformatted output like "15000".
 *
 * @param value - The amount to format. Numbers and numeric strings are accepted.
 * @param cents - Fractional-digit behavior:
 *   - `"always"` (default): always show two decimals → `$82.00`
 *   - `"never"`: never show decimals → `$82`
 *   - `"auto"`: two decimals only when the value has cents → `$82` or `$82.50`
 * @returns The formatted currency string, or `""` for null/undefined/empty/NaN input.
 */
export function formatUSCurrency(value: number | string | null | undefined, cents: CentsDisplay = "always"): string {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const numericValue = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(numericValue)) {
        return "";
    }

    const showCents = cents === "always" || (cents === "auto" && numericValue % 1 !== 0);
    const fractionDigits = showCents ? 2 : 0;

    return numericValue.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        useGrouping: true,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
}
